import {
  faImage,
  faPalette,
  faTrash,
  faVideo,
  faXmark,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { ImageWithLoading } from '@/components/blocks/section-image/image-with-loading';
import { SettingRow } from '@/components/page-editor/settings-panel/fields';
import { ColorPaletteSelector } from '@/components/shared/color-palette-selector';
import { FocusPointOverlay } from '@/components/shared/focus-point-overlay';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MutedText, SmallText } from '@/components/ui/typography';
import { useDebounce } from '@/hooks/use-debounce';
import { buildFocusPointStyle, DEFAULT_FOCUS_POINT, readVideoValue } from '@/lib/prop-utils';
import type { SectionBackgroundType } from '@/lib/section-background';
import { resolveDisplayColor } from '@/lib/site-design/colors';
import { useSiteStylesStore } from '@/stores/site-styles-store';
import { getImageUrl, type ImageValue, isPendingAIImage } from '@/types/image';
import { getVideoUrl, type VideoValue } from '@/types/video';

export type BackgroundType = SectionBackgroundType;

export interface BackgroundDraft {
  backgroundType: BackgroundType;
  backgroundImage?: ImageValue;
  backgroundVideo?: VideoValue;
  backgroundColor: string;
  backgroundOverlay: number;
  backgroundOverlayColor: string;
  backgroundFixed: boolean;
  backgroundFocusX: number;
  backgroundFocusY: number;
}

export interface BackgroundSettingsPanelProps {
  draft: BackgroundDraft;
  onChange: (patch: Partial<BackgroundDraft>) => void;
  onOpenMediaSelector: () => void;
  onRemoveMedia: () => void;
  colorHelperText?: string;
  showFixedBackground?: boolean;
}

interface BackgroundMediaPreviewProps {
  mediaType: 'image' | 'video';
  mediaUrl?: ImageValue | VideoValue;
  focusX?: number;
  focusY?: number;
  onFocusChange?: (x: number, y: number) => void;
  onOpenMediaSelector: () => void;
  onRemoveMedia: () => void;
}

function BackgroundMediaPreview({
  mediaType,
  mediaUrl,
  focusX = DEFAULT_FOCUS_POINT,
  focusY = DEFAULT_FOCUS_POINT,
  onFocusChange,
  onOpenMediaSelector,
  onRemoveMedia,
}: BackgroundMediaPreviewProps) {
  const isVideo = mediaType === 'video';
  const selectLabel = isVideo ? 'Select Video' : 'Select Image';
  const video = isVideo ? readVideoValue(mediaUrl) : undefined;
  const videoUrl = video ? getVideoUrl(video) : undefined;
  const image = !isVideo ? (mediaUrl as ImageValue) : undefined;
  const imageUrl = image && !isPendingAIImage(image) ? getImageUrl(image, 'large') : undefined;
  const focusPosition = `${focusX}% ${focusY}%`;

  return (
    <div className="rounded-md border border-dashed bg-muted/40 p-3">
      {mediaUrl ? (
        <div className="relative h-32 overflow-hidden rounded-md border border-border">
          {isVideo ? (
            <>
              <video
                src={videoUrl}
                muted
                loop
                autoPlay
                playsInline
                preload="metadata"
                className="h-full w-full object-cover"
                style={buildFocusPointStyle(focusX, focusY)}
              />
              {onFocusChange && (
                <FocusPointOverlay focusX={focusX} focusY={focusY} onFocusChange={onFocusChange} />
              )}
            </>
          ) : imageUrl ? (
            <>
              <div
                className="h-full w-full bg-cover bg-no-repeat"
                style={{
                  backgroundImage: `url(${JSON.stringify(imageUrl)})`,
                  backgroundPosition: focusPosition,
                }}
                aria-hidden
              />
              {onFocusChange && (
                <FocusPointOverlay focusX={focusX} focusY={focusY} onFocusChange={onFocusChange} />
              )}
            </>
          ) : (
            <>
              <ImageWithLoading
                image={image}
                alt=""
                className="h-full w-full object-cover"
                style={buildFocusPointStyle(focusX, focusY)}
                fallback={<div className="h-full w-full bg-muted/50" aria-hidden />}
              />
              {onFocusChange && (
                <FocusPointOverlay focusX={focusX} focusY={focusY} onFocusChange={onFocusChange} />
              )}
            </>
          )}
          <div className="absolute right-2 top-2 z-10 flex items-center gap-1.5">
            <Button
              size="sm"
              variant="solid"
              colorScheme="secondary"
              className="h-7 bg-background/90 px-2 backdrop-blur-sm"
              onClick={onOpenMediaSelector}
            >
              <Icon icon={isVideo ? faVideo : faImage} />
              Change
            </Button>
            <Button
              size="icon-sm"
              variant="solid"
              colorScheme="destructive"
              onClick={onRemoveMedia}
            >
              <Icon icon={isVideo ? faXmark : faTrash} />
              <span className="sr-only">{isVideo ? 'Remove video' : 'Remove image'}</span>
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex h-32 items-center justify-center rounded-md border border-border/70 bg-background/60">
          <Button variant="outline" colorScheme="secondary" onClick={onOpenMediaSelector}>
            <Icon icon={isVideo ? faVideo : faImage} />
            {selectLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

export function BackgroundSettingsPanel({
  draft,
  onChange,
  onOpenMediaSelector,
  onRemoveMedia,
  colorHelperText = 'Set a custom background color (overrides site-wide colors).',
  showFixedBackground = true,
}: BackgroundSettingsPanelProps) {
  const showOverlayControl = draft.backgroundType === 'image' || draft.backgroundType === 'video';
  const debouncedOnChange = useDebounce(onChange, 150);
  const siteColors = useSiteStylesStore((s) => s.draft.colors);
  const backgroundColorValue = resolveDisplayColor(draft.backgroundColor, siteColors, undefined);

  return (
    <div className="space-y-3">
      <Tabs
        value={draft.backgroundType}
        onValueChange={(value) => onChange({ backgroundType: value as BackgroundType })}
      >
        <TabsList fullWidth>
          <TabsTrigger value="image">
            <Icon icon={faImage} />
            Image
          </TabsTrigger>
          <TabsTrigger value="video">
            <Icon icon={faVideo} />
            Video
          </TabsTrigger>
          <TabsTrigger value="color">
            <Icon icon={faPalette} />
            Color
          </TabsTrigger>
        </TabsList>

        <TabsContent value="image">
          <BackgroundMediaPreview
            mediaType="image"
            mediaUrl={draft.backgroundImage}
            focusX={draft.backgroundFocusX}
            focusY={draft.backgroundFocusY}
            onFocusChange={(x, y) => onChange({ backgroundFocusX: x, backgroundFocusY: y })}
            onOpenMediaSelector={onOpenMediaSelector}
            onRemoveMedia={onRemoveMedia}
          />
        </TabsContent>

        <TabsContent value="video">
          <BackgroundMediaPreview
            mediaType="video"
            mediaUrl={draft.backgroundVideo}
            focusX={draft.backgroundFocusX}
            focusY={draft.backgroundFocusY}
            onFocusChange={(x, y) => onChange({ backgroundFocusX: x, backgroundFocusY: y })}
            onOpenMediaSelector={onOpenMediaSelector}
            onRemoveMedia={onRemoveMedia}
          />
        </TabsContent>

        <TabsContent value="color">
          <div>
            <ColorPaletteSelector<'backgroundColor'>
              fields={[{ key: 'backgroundColor', label: 'Background Color' }]}
              values={{ backgroundColor: backgroundColorValue }}
              onChange={(_, value) => debouncedOnChange({ backgroundColor: value })}
            />
            <MutedText className="text-xs">{colorHelperText}</MutedText>
          </div>
        </TabsContent>
      </Tabs>

      {showOverlayControl ? (
        <div className="space-y-4">
          <div>
            <ColorPaletteSelector<'backgroundOverlayColor'>
              fields={[{ key: 'backgroundOverlayColor', label: 'Overlay Color' }]}
              values={{ backgroundOverlayColor: draft.backgroundOverlayColor }}
              onChange={(_, value) => debouncedOnChange({ backgroundOverlayColor: value })}
            />
            <MutedText className="text-xs">
              Add a color overlay on top of the {draft.backgroundType}.
            </MutedText>
          </div>

          <div className="space-y-2">
            <SmallText className="leading-5 font-medium text-foreground">Overlay Opacity</SmallText>
            <div className="flex items-center gap-3">
              <Slider
                min={0}
                max={100}
                step={10}
                value={[draft.backgroundOverlay]}
                onValueChange={([value]) => onChange({ backgroundOverlay: value })}
                className="flex-1"
              />
              <MutedText className="w-10 shrink-0 text-right text-xs tabular-nums">
                {draft.backgroundOverlay}%
              </MutedText>
            </div>
          </div>
        </div>
      ) : null}

      {showFixedBackground && draft.backgroundType === 'image' ? (
        <div className="space-y-4">
          <SettingRow
            title="Fixed Background"
            description="Creates a parallax-style background effect while scrolling"
          >
            <Switch
              checked={draft.backgroundFixed}
              onCheckedChange={(checked) => onChange({ backgroundFixed: checked })}
              aria-label="Toggle fixed background"
            />
          </SettingRow>
        </div>
      ) : null}
    </div>
  );
}
